import { useState } from "react";
import clsx from "clsx";
import type { CustomFieldDef } from "@shared/types";
import { useDirectory } from "@/lib/lookups";

export function Field({ label, hint, children, required }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}{required && <span className="text-st-delayed"> *</span>}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-ink-faint">{hint}</span>}
    </label>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={clsx("input", props.className)} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={clsx("input min-h-[80px]", props.className)} />;
}

export function Select({ children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={clsx("input appearance-none", props.className)}>{children}</select>;
}

export function UserPicker({ value, onChange, allowEmpty = true, placeholder = "Unassigned" }: {
  value: string | null; onChange: (v: string | null) => void; allowEmpty?: boolean; placeholder?: string;
}) {
  const users = useDirectory();
  return (
    <Select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      {allowEmpty && <option value="">{placeholder}</option>}
      {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}{u.jobTitle ? ` — ${u.jobTitle}` : ""}</option>)}
    </Select>
  );
}

export function CustomFieldInput({ def, value, onChange }: {
  def: CustomFieldDef; value: unknown; onChange: (v: unknown) => void;
}) {
  const users = useDirectory();
  switch (def.type) {
    case "textarea":
      return <TextArea value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <TextInput type="number" value={(value as number) ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "date":
      return <TextInput type="date" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)} />;
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> {def.label}
        </label>
      );
    case "select":
      return (
        <Select value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {(def.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    case "multiselect":
      return (
        <div className="flex flex-wrap gap-1.5">
          {(def.options ?? []).map((o) => {
            const arr = Array.isArray(value) ? (value as string[]) : [];
            const on = arr.includes(o);
            return (
              <button key={o} type="button"
                onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
                className={clsx("chip border", on ? "border-brand-500 bg-brand-50 text-brand-700" : "border-line")}>
                {o}
              </button>
            );
          })}
        </div>
      );
    case "user":
      return (
        <Select value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
        </Select>
      );
    case "url":
      return <TextInput type="url" placeholder="https://…" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value || null)} />;
    default:
      return <TextInput value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

/** Small inline-editable cell used by the schedule grid. */
export function EditableCell({ value, onSave, type = "text", options, align = "left", className }: {
  value: string | number | null;
  onSave: (v: string) => void;
  type?: "text" | "number" | "date" | "select";
  options?: { value: string; label: string }[];
  align?: "left" | "right" | "center";
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  if (editing) {
    if (type === "select") {
      return (
        <select
          autoFocus
          className="w-full bg-surface px-1 py-1 text-sm outline-none ring-1 ring-brand-500"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); onSave(e.target.value); setEditing(false); }}
          onBlur={() => setEditing(false)}
        >
          {options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        autoFocus
        type={type}
        className="w-full bg-surface px-1 py-1 text-sm outline-none ring-1 ring-brand-500"
        style={{ textAlign: align }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { onSave(draft); setEditing(false); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onSave(draft); setEditing(false); }
          if (e.key === "Escape") { setDraft(String(value ?? "")); setEditing(false); }
        }}
      />
    );
  }
  return (
    <button
      className={clsx("block w-full truncate px-1 py-1 text-sm hover:bg-surface-2", className)}
      style={{ textAlign: align }}
      onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
    >
      {options ? (options.find((o) => o.value === String(value))?.label ?? "—") : (value ?? <span className="text-ink-faint">—</span>)}
    </button>
  );
}
